/**
 * Citation Verification Tool - Citation Extractor
 * 
 * Extracts citations and references from manuscripts using LLM parsing
 */

import { createFileLogger, formatJSON } from '../../../utils/logger';
import { CommandExecutionContext } from '../../../types/commands';
import {
  ExtractionResult,
  InTextCitation,
  Reference,
  ParsedReference,
  CitationFormat
} from './types';
import {
  detectCitationFormat,
  analyzeClaimStrength,
  extractDOI,
  extractPMID,
  extractArxivId,
  createCitationId,
  createReferenceId
} from './utils';

const log = createFileLogger('VERIFY_CITATIONS_EXTRACTOR');

/**
 * Extract citations and references from a manuscript
 */
export async function extractCitations(
  manuscriptText: string,
  manuscriptId: string,
  context: CommandExecutionContext
): Promise<ExtractionResult> {
  log.info('[extractCitations] 📄 Starting extraction...');
  log.info(formatJSON({
    manuscriptId,
    textLength: manuscriptText.length,
    hasReferencesSection: manuscriptText.toLowerCase().includes('references'),
    hasBibliographySection: manuscriptText.toLowerCase().includes('bibliography'),
    textStart: manuscriptText.substring(0, 200),
    textEnd: manuscriptText.substring(Math.max(0, manuscriptText.length - 500))
  }, '📤 Manuscript Analysis'));
  
  // Use LLM to parse the manuscript
  const extractionPrompt = buildExtractionPrompt(manuscriptText);
  log.info(formatJSON({
    promptLength: extractionPrompt.length,
    promptPreview: extractionPrompt.substring(0, 500)
  }, '📤 Extraction Prompt'));
  
  // Call LLM for extraction
  const llmResponse = await callLLMForExtraction(extractionPrompt, context);
  log.info(formatJSON({
    hasResponse: !!llmResponse,
    responseType: typeof llmResponse,
    responseKeys: Object.keys(llmResponse || {})
  }, '📥 LLM Response Received'));
  
  // Parse LLM response
  const extracted = parseLLMExtraction(llmResponse);
  
  // Update citation locations with actual positions from manuscript
  log.info('[extractCitations] Manuscript text analysis:', {
    manuscriptLength: manuscriptText.length,
    startsWithComment: manuscriptText.startsWith('<!--'),
    firstHundredChars: manuscriptText.substring(0, 100),
    lastHundredChars: manuscriptText.substring(Math.max(0, manuscriptText.length - 100))
  });
  
  for (const citation of extracted.inTextCitations) {
    // Prefer LLM's estimated position if provided
    const llmEstimatedPos = (citation as any).estimatedCharPosition;
    
    log.info(`[extractCitations] Processing citation "${citation.citationKey}":`, {
      llmEstimatedPos,
      citationKeyLength: citation.citationKey.length
    });
    
    if (llmEstimatedPos !== undefined && typeof llmEstimatedPos === 'number') {
      // Validate the LLM's estimate by checking nearby text (±200 chars)
      const searchStart = Math.max(0, llmEstimatedPos - 200);
      const searchEnd = Math.min(manuscriptText.length, llmEstimatedPos + 200);
      const nearbyText = manuscriptText.substring(searchStart, searchEnd);
      
      log.info(`[extractCitations] Checking near LLM estimate:`, {
        searchStart,
        searchEnd,
        nearbyTextLength: nearbyText.length,
        nearbyPreview: nearbyText.substring(0, 100)
      });
      
      // Look for the citation key in the nearby region
      const localIndex = nearbyText.indexOf(citation.citationKey);
      if (localIndex !== -1) {
        const actualPos = searchStart + localIndex;
        citation.location.charOffset = actualPos;
        citation.location.charLength = citation.citationKey.length;
        citation.location.nodeId = '';
        log.info(`[extractCitations] ✅ Found citation "${citation.citationKey}" near LLM estimate (offset: ${actualPos - llmEstimatedPos})`);
        continue;
      }
      
      // LLM estimate didn't pan out, but note it was provided
      log.warn(`[extractCitations] LLM provided position ${llmEstimatedPos} but citation not found nearby`);
    }
    
    // Fall back to exact string search
    log.info(`[extractCitations] Trying exact match for "${citation.citationKey}"`);
    const citationIndex = manuscriptText.indexOf(citation.citationKey);
    if (citationIndex !== -1) {
      citation.location.charOffset = citationIndex;
      citation.location.charLength = citation.citationKey.length;
      citation.location.nodeId = '';
      log.info(`[extractCitations] ✅ Found citation "${citation.citationKey}" at position ${citationIndex} (exact match)`);
      continue;
    }
    
    // Try normalized search
    log.info(`[extractCitations] Trying normalized match for "${citation.citationKey}"`);
    const normalizedKey = citation.citationKey.replace(/\s+/g, ' ').trim();
    const normalizedIndex = manuscriptText.replace(/\s+/g, ' ').indexOf(normalizedKey);
    if (normalizedIndex !== -1) {
      citation.location.charOffset = normalizedIndex;
      citation.location.charLength = normalizedKey.length;
      citation.location.nodeId = '';
      log.info(`[extractCitations] ✅ Found normalized citation "${citation.citationKey}" at position ${normalizedIndex}`);
      continue;
    }
    
    // Last resort: try finding as part of a multi-citation
    log.info(`[extractCitations] Trying multi-citation pattern for "${citation.citationKey}"`);
    const coreKey = citation.citationKey.replace(/^\(|\)$/g, '').trim();
    const multiCitationPattern = new RegExp(`\\([^)]*${escapeRegex(coreKey)}[^)]*\\)`);
    const multiMatch = manuscriptText.match(multiCitationPattern);
    
    if (multiMatch && multiMatch.index !== undefined) {
      // Found as part of multi-citation - use that position
      citation.location.charOffset = multiMatch.index;
      citation.location.charLength = multiMatch[0].length;
      citation.location.nodeId = '';
      log.info(`[extractCitations] ✅ Found citation "${citation.citationKey}" as part of multi-citation "${multiMatch[0]}" at position ${multiMatch.index}`);
      continue;
    }
    
    // Could not find citation
    citation.location.charOffset = -1;
    citation.location.charLength = 0;
    citation.location.nodeId = '';
    log.warn(`[extractCitations] ❌ Could not find citation "${citation.citationKey}" in manuscript text`);
  }
  
  // Helper to escape special regex characters
  function escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
  
  // Detect citation format
  const citationKeys = extracted.inTextCitations.map(c => c.citationKey);
  const detectedFormat = detectCitationFormat(citationKeys);
  
  // Match citations to references
  log.info('[extractCitations] 🔗 Matching citations to references...');
  const citationReferenceMap = matchCitationsToReferences(
    extracted.inTextCitations,
    extracted.references,
    detectedFormat
  );
  
  // Find unmatched citations
  const unmatchedCitations = extracted.inTextCitations
    .filter(c => !citationReferenceMap.has(c.id))
    .map(c => c.citationKey);
  
  log.info(formatJSON({
    format: detectedFormat,
    citationsFound: extracted.inTextCitations.length,
    referencesFound: extracted.references.length,
    matchedCount: citationReferenceMap.size,
    unmatchedCount: unmatchedCitations.length,
    unmatchedCitations: unmatchedCitations.slice(0, 10)
  }, '✅ Extraction Complete'));
  
  return {
    citationFormat: detectedFormat,
    inTextCitations: extracted.inTextCitations,
    references: extracted.references,
    citationReferenceMap,
    unmatchedCitations,
    ambiguousCitations: [] // Will be populated during matching
  };
}

/**
 * Build extraction prompt for LLM
 */
function buildExtractionPrompt(manuscriptText: string): string {
  return `You are a citation extraction expert. Analyze this academic manuscript and extract all citations and references.

MANUSCRIPT TEXT:
${manuscriptText.substring(0, 50000)} ${manuscriptText.length > 50000 ? '\n\n[Text truncated for length]' : ''}

TASK:
1. Find all in-text citations (e.g., [1], [12], (Smith, 2017), Smith et al. 2017)
2. Extract the sentence or claim associated with each citation
3. Estimate the character position where each citation appears
4. Find the reference list (usually at the end)
5. Parse each reference entry

IMPORTANT: For multi-citations like "(Smith, 2017; Jones, 2018)", extract each citation separately with its own position.

OUTPUT FORMAT (JSON):
{
  "inTextCitations": [
    {
      "citationKey": "[1]",
      "claimText": "The full sentence containing the citation.",
      "approximateLocation": "beginning|middle|end",
      "estimatedCharPosition": 1234
    }
  ],
  "references": [
    {
      "listPosition": 1,
      "rawText": "The full reference text as written",
      "authors": ["Smith, J.", "Jones, M."],
      "title": "Paper title",
      "year": 2020,
      "journal": "Journal Name",
      "doi": "10.xxxx/xxxxx",
      "volume": "12",
      "pages": "123-145"
    }
  ]
}

CRITICAL INSTRUCTIONS:
- **SPLIT MULTIPLE CITATIONS:** If you see multiple citations in one parenthesis like "(Manthey et al., 2022; WHO, 2025)", create SEPARATE entries for EACH citation:
  - First entry: citationKey = "(Manthey et al., 2022)"
  - Second entry: citationKey = "(WHO, 2025)"
  - Both entries get the SAME claimText (same sentence)
- Extract ALL citations, even if there are many
- Include the complete sentence for each citation (the claim being made)
- Parse references carefully - extract DOI, PMID, arXiv ID if present
- If a field is missing or unclear, omit it from the JSON
- Be consistent with citation format detection

Respond with ONLY the JSON object, no other text.`;
}

/**
 * Call LLM for citation extraction
 */
async function callLLMForExtraction(
  prompt: string,
  context: CommandExecutionContext
): Promise<any> {
  log.info('[callLLMForExtraction] 🤖 Calling LLM for extraction...');
  
  // Use Claude for extraction
  const { API_BASE_URL } = await import('../../../api/config');
  
  const authToken = localStorage.getItem('authToken');
  
  const requestBody = {
    query: prompt,
    context: {
      spaceId: context.spaceId || 'default',
      windowContents: {},
      explicitContent: []
    },
    conversation_history: [],
    model: 'claude-sonnet-4-5',
    temperature: 0.1 // Low temperature for consistent extraction
  };
  
  log.info(formatJSON({
    endpoint: `${API_BASE_URL}/v2/claude/chat/stream`,
    method: 'POST',
    hasAuth: !!authToken,
    model: requestBody.model,
    temperature: requestBody.temperature,
    promptLength: prompt.length,
    spaceId: context.spaceId
  }, '📤 LLM Request'));
  
  const requestStart = Date.now();
  const response = await fetch(`${API_BASE_URL}/v2/claude/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    credentials: 'include',
    body: JSON.stringify(requestBody)
  });
  const requestTime = Date.now() - requestStart;
  
  log.info(formatJSON({
    status: response.status,
    statusText: response.statusText,
    contentType: response.headers.get('content-type'),
    requestTimeMs: requestTime
  }, '📥 LLM Response Headers'));
  
  if (!response.ok) {
    const errorText = await response.text();
    log.error(formatJSON({
      status: response.status,
      statusText: response.statusText,
      errorText: errorText.substring(0, 1000)
    }, '⚠️ LLM Request Failed'));
    throw new Error(`LLM extraction failed: ${response.status} - ${errorText}`);
  }
  
  // Read the streaming response and accumulate text
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Failed to get response reader');
  }
  
  const decoder = new TextDecoder();
  let buffer = '';
  let fullText = '';
  let chunkCount = 0;
  let textChunkCount = 0;
  
  try {
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) break;
      
      buffer += decoder.decode(value, { stream: true });
      
      // Process complete lines (NDJSON format)
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer
      
      for (const line of lines) {
        if (!line.trim()) continue;
        
        chunkCount++;
        
        try {
          const parsed = JSON.parse(line);
          
          // Log first few chunks to see format
          if (chunkCount <= 3) {
            log.log(`[callLLMForExtraction] Stream chunk #${chunkCount}`, parsed);
          }
          
          // Extract text from streaming events - handle multiple formats
          if (parsed.type === 'content_block_delta' && parsed.delta?.type === 'text_delta') {
            // Standard Anthropic streaming format
            fullText += parsed.delta.text;
            textChunkCount++;
          } else if (parsed.type === 'text' && parsed.content) {
            // Backend's custom streaming format
            fullText += parsed.content;
            textChunkCount++;
          }
        } catch (e) {
          log.warn('[callLLMForExtraction] Failed to parse line:', line);
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
  
  log.info(formatJSON({
    totalChunks: chunkCount,
    textChunks: textChunkCount,
    textLength: fullText.length,
    hasContent: fullText.length > 0,
    textPreview: fullText.substring(0, 500),
    textEnd: fullText.substring(Math.max(0, fullText.length - 300))
  }, '📥 Stream Complete'));
  
  if (fullText.length === 0) {
    log.error('[callLLMForExtraction] ⚠️ LLM returned empty text!');
  }
  
  return { content: fullText, text: fullText };
}

/**
 * Split multi-citations like "(Manthey et al., 2022; WHO, 2025)" into separate citations
 */
function splitMultiCitation(citation: any): any[] {
  const key = citation.citationKey || '';
  
  // Check if this looks like a multi-citation (semicolon-separated)
  // Pattern: (Author1, Year1; Author2, Year2)
  if (!key.includes(';')) {
    return [citation];
  }
  
  // Remove outer parentheses if present
  const innerKey = key.replace(/^\(/, '').replace(/\)$/, '');
  
  // Split by semicolon
  const parts = innerKey.split(';').map((p: string) => p.trim());
  
  // If only one part after splitting, not a multi-citation
  if (parts.length <= 1) {
    return [citation];
  }
  
  log.info('[splitMultiCitation] Splitting multi-citation:', {
    original: key,
    parts: parts
  });
  
  // Create separate citation entries for each part
  return parts.map((part: string) => ({
    ...citation,
    citationKey: `(${part})` // Re-wrap in parentheses
  }));
}

/**
 * Parse LLM extraction response
 */
function parseLLMExtraction(llmResponse: any): {
  inTextCitations: InTextCitation[];
  references: Reference[];
} {
  log.info('[parseLLMExtraction] 🔍 Parsing LLM response...');
  log.info(formatJSON(llmResponse, '📥 Full LLM Response', { maxObjectDepth: 3, maxStringLength: 500 }));
  
  // Extract JSON from LLM response
  let extractedData: any;
  try {
    // Try to find JSON in the response
    const responseText = llmResponse.content || llmResponse.text || JSON.stringify(llmResponse);
    log.info(formatJSON({
      textLength: responseText.length,
      textPreview: responseText.substring(0, 1000),
      textEnd: responseText.substring(Math.max(0, responseText.length - 500))
    }, '📄 Response Text Analysis'));
    
    // Look for JSON object
    const jsonMatch = responseText.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      log.info('[parseLLMExtraction] ✅ Found JSON in response');
      log.info(formatJSON({
        jsonLength: jsonMatch[0].length,
        jsonPreview: jsonMatch[0].substring(0, 1000)
      }, '📄 JSON Match'));
      
      extractedData = JSON.parse(jsonMatch[0]);
      log.info(formatJSON({
        hasInTextCitations: !!extractedData.inTextCitations,
        hasReferences: !!extractedData.references,
        citationsCount: (extractedData.inTextCitations || []).length,
        referencesCount: (extractedData.references || []).length,
        keys: Object.keys(extractedData)
      }, '📊 Extracted Data Summary'));
      
      // Log details about what was found
      if ((extractedData.references || []).length === 0) {
        log.warn('[parseLLMExtraction] ⚠️ NO REFERENCES FOUND IN LLM RESPONSE!');
        log.warn(formatJSON({
          fullExtractedData: extractedData
        }, '⚠️ Complete Extracted Data (no references)', { maxObjectDepth: 5 }));
      } else {
        log.info(formatJSON({
          references: extractedData.references
        }, '📚 References Found', { maxObjectDepth: 3 }));
      }
      
      if ((extractedData.inTextCitations || []).length > 0) {
        log.info(formatJSON({
          sampleCitations: extractedData.inTextCitations.slice(0, 3)
        }, '📝 Sample Citations', { maxObjectDepth: 3 }));
      }
    } else {
      log.error('[parseLLMExtraction] ❌ No JSON found in response text!');
      log.error(formatJSON({
        fullResponseText: responseText
      }, '⚠️ Response With No JSON', { maxStringLength: 2000 }));
      throw new Error('No JSON found in LLM response');
    }
  } catch (error) {
    log.error('[parseLLMExtraction] ❌ Failed to parse JSON');
    log.error(formatJSON({
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : error,
      responseText: (llmResponse.content || llmResponse.text || '').substring(0, 1000)
    }, '⚠️ Parse Error Context'));
    throw new Error('Failed to parse LLM extraction response');
  }
  
  // Post-process: Split multi-citations that LLM may have missed
  const processedCitations: any[] = [];
  for (const c of extractedData.inTextCitations || []) {
    const splitCitations = splitMultiCitation(c);
    processedCitations.push(...splitCitations);
  }
  
  log.info('[parseLLMExtraction] Multi-citation splitting:', {
    originalCount: (extractedData.inTextCitations || []).length,
    afterSplitting: processedCitations.length,
    splitOccurred: processedCitations.length > (extractedData.inTextCitations || []).length
  });
  
  // Convert to our format
  const inTextCitations: InTextCitation[] = processedCitations.map((c: any, idx: number) => {
    const claimStrength = analyzeClaimStrength(c.claimText || '');
    
    return {
      id: createCitationId(c.citationKey || `unknown_${idx}`, idx),
      citationKey: c.citationKey || `[${idx + 1}]`,
      claimText: c.claimText || '',
      claimStrength,
      location: {
        nodeId: '', // Will be populated in extractCitations
        charOffset: 0,
        charLength: (c.claimText || '').length
      },
      linkedReferenceIds: [] // Will be populated during matching
    };
  });
  
  const references: Reference[] = (extractedData.references || []).map((r: any, idx: number) => {
    const parsed: ParsedReference = {
      authors: r.authors || [],
      title: r.title,
      year: r.year,
      journal: r.journal,
      volume: r.volume,
      issue: r.issue,
      pages: r.pages,
      doi: r.doi || extractDOI(r.rawText || ''),
      pmid: r.pmid || extractPMID(r.rawText || ''),
      arxivId: r.arxivId || extractArxivId(r.rawText || ''),
      url: r.url
    };
    
    return {
      id: createReferenceId(idx),
      listPosition: r.listPosition || idx + 1,
      rawText: r.rawText || '',
      parsed,
      parseConfidence: calculateParseConfidence(parsed)
    };
  });
  
  return { inTextCitations, references };
}

/**
 * Calculate confidence in reference parsing
 */
function calculateParseConfidence(parsed: ParsedReference): number {
  let score = 0;
  
  if (parsed.authors && parsed.authors.length > 0) score += 20;
  if (parsed.title) score += 30;
  if (parsed.year) score += 20;
  if (parsed.journal) score += 10;
  if (parsed.doi) score += 20;
  
  return Math.min(score, 100);
}

/**
 * Match in-text citations to reference list entries
 */
function matchCitationsToReferences(
  citations: InTextCitation[],
  references: Reference[],
  format: CitationFormat
): Map<string, string> {
  const map = new Map<string, string>();
  
  if (format === 'numbered') {
    // For numbered citations, match by number
    for (const citation of citations) {
      const numberMatch = citation.citationKey.match(/\d+/);
      if (numberMatch) {
        const num = parseInt(numberMatch[0], 10);
        const reference = references.find(r => r.listPosition === num);
        if (reference) {
          map.set(citation.id, reference.id);
          citation.linkedReferenceIds = [reference.id];
        }
      }
    }
  } else if (format === 'author_year') {
    // For author-year citations, match by author and year
    for (const citation of citations) {
      // Extract author and year from citation key
      const authorMatch = citation.citationKey.match(/([A-Z][a-z]+)/);
      const yearMatch = citation.citationKey.match(/(\d{4})/);
      
      if (authorMatch && yearMatch) {
        const author = authorMatch[1];
        const year = parseInt(yearMatch[1], 10);
        
        // Find matching reference
        const reference = references.find(r => {
          const hasMatchingAuthor = r.parsed.authors?.some(a => 
            a.toLowerCase().includes(author.toLowerCase())
          );
          const hasMatchingYear = r.parsed.year === year;
          return hasMatchingAuthor && hasMatchingYear;
        });
        
        if (reference) {
          map.set(citation.id, reference.id);
          citation.linkedReferenceIds = [reference.id];
        }
      }
    }
  }
  
  return map;
}

